// Story 1.21 — TanStack mutation for POST /api/v1/creators/apply.
//
// Fresh `crypto.randomUUID()` idempotency key per submit. The error
// shape preserves the discriminated `reason` + per-link `failures`
// fields so the form can highlight specific rows on a 422.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type LinkedAccountInput = {
  kind: string;
  url: string;
};

export type SubmitApplicationRequest = {
  display_name: string;
  hello_text: string;
  image_blob_path: string;
  signature_blob_path: string;
  linked_accounts: LinkedAccountInput[];
  region: string;
  language: string;
  tos_version_accepted_id: string;
  // Story 8.9 — who we contract with and pay. Flat on the wire; the server
  // validates the whole block in one pass and names the offending field.
  //
  // There is deliberately NO `tin` here. The column exists and is dormant;
  // see server/migrations/0094_creator_identity.sql before adding one.
  legal_name: string;
  address_line1: string;
  address_line2?: string;
  postal_code: string;
  city: string;
  country_code: string;
  business_status: "private" | "business";
  /** Sent only by a business applicant, and only when they gave one. */
  business_registration_number?: string;
};

export type SubmitApplicationResponse = {
  application_id: string;
  status: "pending";
  submitted_at: string;
  review_eta_hours: number;
};

export type LinkedAccountFailureDetail = {
  index: number;
  kind: string;
  url: string;
  reason: string;
};

export type SubmitApplicationError = {
  code: string;
  message: string;
  reason?: string;
  field?: string;
  existing_application_id?: string | null;
  failures?: LinkedAccountFailureDetail[];
};

async function postApply(
  body: SubmitApplicationRequest,
  idempotencyKey: string,
): Promise<SubmitApplicationResponse> {
  const response = await fetch("/api/v1/creators/apply", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: SubmitApplicationError = {
      code: "CREATOR_APPLICATION_SUBMIT_FAILED",
      message: `Submit failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  const envelope = (await response.json()) as {
    data: SubmitApplicationResponse;
  };
  return envelope.data;
}

export function useSubmitCreatorApplication() {
  const queryClient = useQueryClient();
  return useMutation<
    SubmitApplicationResponse,
    SubmitApplicationError,
    SubmitApplicationRequest
  >({
    mutationFn: (body) => postApply(body, crypto.randomUUID()),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["creator-application-status"],
      });
    },
  });
}
